import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import CircularProgress from '@material-ui/core/CircularProgress';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import { Row } from 'components/UI/Row';
import { saveDelegate, getUsersApiErrors, getUsersLoaders } from './users';
import { UserFormProps, UserFormSchema, UpdateUserSchema, UpdateUserPasswordSchema, UserType } from './userFormSchema';

const UserForm: FC<{ closeFn: () => void; isOpen: boolean; updatePassword?: boolean; user?: any }> = ({
  isOpen,
  closeFn,
  updatePassword,
  user
}) => {
  const { t } = useTranslation();
  const schema = !user ? UserFormSchema : UpdateUserSchema;
  const { handleSubmit, errors, control, setValue } = useForm<UserFormProps>({
    mode: 'onBlur',
    resolver: yupResolver(updatePassword ? UpdateUserPasswordSchema : schema)
  });
  const dispatch = useDispatch();
  const { isSaving } = useSelector(getUsersLoaders);
  const { savingError } = useSelector(getUsersApiErrors);
  const [waitForResponse, setWaitForResponse] = useState<boolean>(false);

  // TODO: make a custom hook
  useEffect(() => {
    if (!waitForResponse && !savingError) {
      closeFn();
    }
  }, [waitForResponse, savingError]);

  useEffect(() => {
    if (user) {
      setTimeout(() => {
        const { email, first_name, last_name, role } = user;
        setValue('email', email);
        setValue('first_name', first_name);
        setValue('last_name', last_name);
        setValue('role', role);
      });
    }
  }, [setValue, user]);

  const onSubmit = async (formData: UserFormProps) => {
    setWaitForResponse(true);
    await dispatch(saveDelegate({ user: formData, id: user?.id }));
    setWaitForResponse(false);
  };

  return (
    <Dialog maxWidth="xs" open={isOpen} onClose={closeFn} aria-labelledby="site-form-title">
      <DialogTitle id="site-form-title">{user?.id ? t('delegates.edit user') : t('delegates.new user')}</DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          {!updatePassword && (
            <>
              {' '}
              <Controller
                as={
                  <TextField
                    autoComplete="new-password"
                    helperText={t(get(errors, 'email.message'))}
                    error={!isEmpty(get(errors, `email`))}
                    label={t('label.email')}
                    type="email"
                    style={{ marginBottom: '16px', width: '400px' }}
                    variant="outlined"
                  />
                }
                control={control}
                defaultValue=""
                name="email"
              />
              <FormControl variant="outlined" fullWidth error={!isEmpty(get(errors, `role`))}>
                <InputLabel id="role-label">{t('delegates.role')}</InputLabel>
                <Controller
                  as={
                    <Select
                      value=""
                      labelId="role-label"
                      input={<OutlinedInput label={t('delegates.role')} />}
                      style={{ marginBottom: '16px', width: '400px' }}
                    >
                      <MenuItem value="">{t('delegates.roles.select')}</MenuItem>
                      <MenuItem value={UserType.CompanyAlias}>{t('delegates.roles.manager')}</MenuItem>
                      <MenuItem value={UserType.Supervisor}>{t('delegates.roles.worker')}</MenuItem>
                    </Select>
                  }
                  control={control}
                  defaultValue=""
                  name="role"
                />
                <FormHelperText
                  style={{
                    marginBottom: '16px',
                    marginTop: '-10px'
                  }}
                >
                  {t(get(errors, 'role.message'))}
                </FormHelperText>
              </FormControl>
              <Controller
                as={
                  <TextField
                    autoComplete="new-password"
                    helperText={t(get(errors, 'first_name.message'))}
                    error={!isEmpty(get(errors, `first_name`))}
                    label={t('label.first name')}
                    style={{ marginBottom: '16px', width: '400px' }}
                    variant="outlined"
                  />
                }
                control={control}
                defaultValue=""
                name="first_name"
              />
              <Controller
                as={
                  <TextField
                    autoComplete="new-password"
                    helperText={t(get(errors, 'last_name.message'))}
                    error={!isEmpty(get(errors, `last_name`))}
                    label={t('label.last name')}
                    style={{ marginBottom: '16px', width: '400px' }}
                    variant="outlined"
                  />
                }
                control={control}
                defaultValue=""
                name="last_name"
              />
            </>
          )}

          {(!user?.id || updatePassword) && (
            <>
              <Controller
                as={
                  <TextField
                    autoComplete="new-password"
                    helperText={t(get(errors, 'password.message'))}
                    error={!isEmpty(get(errors, `password`))}
                    label={t('label.password')}
                    type="password"
                    style={{ marginBottom: '16px', width: '400px' }}
                    variant="outlined"
                  />
                }
                control={control}
                defaultValue=""
                name="password"
              />

              <Controller
                as={
                  <TextField
                    autoComplete="new-password"
                    helperText={t(get(errors, 'passwordCompare.message'))}
                    error={!isEmpty(get(errors, `passwordCompare`))}
                    label={t('label.confirm password')}
                    type="password"
                    style={{ marginBottom: '16px', width: '400px' }}
                    variant="outlined"
                  />
                }
                control={control}
                defaultValue=""
                name="passwordCompare"
              />
            </>
          )}
          <Row margin="20px 0" justifyContent="space-between">
            <Button color="secondary" onClick={closeFn}>
              {t('label.cancel')}
            </Button>
            <Button type="submit" color="primary" disabled={isSaving}>
              {t('label.save')}
              {isSaving && (
                <CircularProgress
                  color="primary"
                  size={34}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    marginTop: -17,
                    marginLeft: -17
                  }}
                />
              )}
            </Button>
          </Row>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export { UserForm };
